// Client mirror of the C7 schedule wire types.
//
// SYNC: every type here must match src/gateway/types.ts's "C7 Schedules"
// block. Change one, change the other — same discipline as EffortLevel in
// chat-config.ts.

export type ScheduleIdentity = "rook" | "rainer"

export type ScheduleKind = "task" | "reminder" | "cadence"

export type ScheduleDesiredState = "armed" | "paused"

export type ScheduleSyncStatus =
  | "ok"
  | "pending"
  | "orphaned"
  | "unreachable"
  | "unmanaged"
  | "completed"
  | "recovering"

export interface ScheduleRow {
  id: string
  identity: ScheduleIdentity
  kind: ScheduleKind
  name: string
  prompt: string | null
  schedule: string
  schedule_display: string
  desired_state: ScheduleDesiredState
  hermes_job_id: string | null
  hermes_enabled: number | null
  hermes_state: string | null
  next_run_at: string | null
  last_run_at: string | null
  last_status: string | null
  last_error: string | null
  repeat_times: number | null
  repeat_completed: number | null
  sync_status: ScheduleSyncStatus
  reconciled_at: string | null
  created_at: string
  updated_at: string
  deleted_at: string | null
  /** The chat this wake was scheduled from; `null` for Settings-born wakes.
   *  Slice 1 added this gateway-side; day one has no client that reads it —
   *  slice 2′ is that client. */
  origin_session_id: string | null
  /** Who created this row. `null` means the row predates this column and was
   *  Falco's (Settings-born) by construction — same as `'falco'`. */
  created_by: "falco" | "companion" | null
  /** ADR-024: "claude" rows have no Hermes job; the gateway fires them itself as the real companion. */
  engine: ScheduleEngine
  /** ADR-024 A2.4: when Falco cleared the last outcome; absent on a gateway that predates the shelf. */
  cleared_at?: string | null
  /** ADR-024 A2.4: server-computed. Settings does not act on it yet (no History view or Clear button until the mockups): every row is still listed. */
  shelf?: ScheduleShelf
}

export type ScheduleShelf = "active" | "history" | "pinned"

export type ScheduleEngine = "hermes" | "claude"

/** ADR-024 door_runs.status. */
export type DoorRunStatus =
  | "running"
  | "done"
  | "timed_out"
  | "interrupted"
  | "failed"
  | "missed"
  | "held_budget"
  | "held_quota"
  | "deferred"

/** One row of the door ledger (the fields Settings reads). */
export interface DoorRun {
  id: string
  schedule_id: string
  identity: ScheduleIdentity
  status: DoorRunStatus
  trigger: "clock" | "run_now"
  started_at: string
  duration_ms: number | null
}

/** One companion's budget line. */
export interface ScheduleDoorCompanion {
  enabled: boolean
  used_this_week: number
  weekly_limit: number
  resets_at: string
  max_minutes: number
  holding: string | null
}

export interface ScheduleRun {
  id: string
  schedule_id: string
  identity: ScheduleIdentity
  kind: ScheduleKind
  source: "muse" | "hermes"
  fired_at: string
  status: string | null
  error: string | null
  observed_at: string
}

export type ScheduleKillEngageStatus = "ok" | "partial" | "failed"

export interface ScheduleKillDetail {
  identity: ScheduleIdentity
  hermes_job_id: string | null
  ok: boolean
  error?: string
}

export interface ScheduleKillSwitchState {
  on: boolean
  engaged_at: string | null
  engage_status: ScheduleKillEngageStatus | null
  engage_detail: ScheduleKillDetail[] | null
}

export interface ScheduleHermesInfo {
  reachable: boolean
  model_label?: string
}

export interface ScheduleListView {
  schedules: ScheduleRow[]
  runs_recent: ScheduleRun[]
  kill_switch: ScheduleKillSwitchState
  hermes: Partial<Record<ScheduleIdentity, ScheduleHermesInfo>>
  reconciled_at: string | null
  /** ADR-024; null when the gateway has no door. */
  door: { companions: Record<ScheduleIdentity, ScheduleDoorCompanion> } | null
  door_runs: DoorRun[]
}

/** Companion display name for a row's `who` column. */
export function scheduleWho(row: ScheduleRow): string {
  if (row.kind === "reminder") return "you"
  return row.identity === "rook" ? "Rook" : "Rainer"
}

/**
 * True when Hermes still has the job enabled although the user asked for
 * paused — i.e. a run-now the reconciler has not re-paused yet.
 */
export function isRePausing(row: ScheduleRow): boolean {
  return row.desired_state === "paused" && row.hermes_enabled === 1
}
